import Service, { service } from '@ember/service';
import {
  isPslfDebugActive,
  getPslfDebugState,
  buildMockLoanSummary,
  DEBUG_RECOMMENDATION,
} from 'tio-employee/utils/pslf/debug-scenarios';
import type { PslfDebugState, DebugLoan } from 'tio-employee/utils/pslf/debug-scenarios';
import type PersonModel from 'tio-common/models/person';
import type PslfFormModel from 'tio-common/models/pslf-form';
import type PslfProfileModel from 'tio-common/models/pslf-profile';
import type RecommendationModel from 'tio-common/models/recommendation';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type StoreService from 'ember-data/store';

export interface DashboardModel {
  accounts: PersonModel['accounts'];
  loanSummary: LoanSummary;
  mostRecentRecommendation: RecommendationModel | undefined;
  person: PersonModel;
  pslfProfile: PslfProfileModel;
  pslfForms: PslfFormModel[];
  isV2: boolean;
  debugLinkLoans: boolean;
}

export default class PslfDebugService extends Service {
  @service declare store: StoreService;

  isActive() {
    return isPslfDebugActive();
  }

  getState(): PslfDebugState {
    return getPslfDebugState();
  }

  /**
   * Apply all debug overrides to the route model in a single call.
   * Pushes mock records into the Ember Data store (forms, accounts, loans,
   * recommendation) and returns the mutated model.
   */
  applyTo(model: DashboardModel): DashboardModel {
    const debugState = this.getState();
    const result = { ...model };

    // 1. Push debug PSLF forms into the store
    result.pslfForms = this.pushDebugForms(model.pslfProfile, debugState);

    // 2. Mock loan summary (drives recommendation logic)
    result.loanSummary = buildMockLoanSummary(debugState);

    // 3. Mock recommendation (estimator results)
    if (debugState.estimatorCompleted || debugState.recertifyIncome) {
      result.mostRecentRecommendation = this.pushDebugRecommendation();
    } else {
      result.mostRecentRecommendation = undefined;
    }

    // 4. NSLDS accounts
    if (debugState.hasNsldsData) {
      this.pushDebugAccounts(model.person, debugState);
    } else {
      // Debug mode + NSLDS off => unload accounts to simulate "no loans"
      this.unloadAccounts(model.person);
    }

    // 5. Link loans flag
    result.debugLinkLoans = debugState.linkLoans;

    return result;
  }

  // ---------------------------------------------------------------------------
  // Form pushing
  // ---------------------------------------------------------------------------

  private pushDebugForms(
    pslfProfile: PslfProfileModel,
    debugState: PslfDebugState
  ): PslfFormModel[] {
    const debugForms = debugState.debugForms ?? [];
    if (debugForms.length === 0) return [];

    // Unload prior debug forms
    const existing = pslfProfile.pslfForms?.slice() ?? [];
    for (const form of existing) {
      if ((form.id as string).startsWith('debug-form-')) {
        form.unloadRecord();
      }
    }

    // Push each debug form as a real pslf-form record
    for (const debugForm of debugForms) {
      const startDate = debugForm.employmentStartDate ?? '2024-01-15';
      const borrowerFields: Record<string, string> = {
        'borrower-employer-name': debugForm.employerName,
        'borrower-employment-start-date': startDate,
      };
      if (debugForm.formType === 'priorEmployer' && debugForm.employmentEndDate) {
        borrowerFields['borrower-employment-end-date'] = debugForm.employmentEndDate;
      }

      this.store.push({
        data: {
          id: debugForm.id,
          type: 'pslf-form',
          attributes: {
            status: debugForm.status,
            formType: debugForm.formType,
            borrowerCompletedAt: debugForm.borrowerCompletedAt,
            employerCompletedAt: debugForm.employerCompletedAt,
            submittedAt: debugForm.submittedAt,
            createdAt: debugForm.borrowerCompletedAt ?? new Date().toISOString(),
            borrowerFields,
            employerFields: {},
            isNonPartner: debugForm.nonPartner ?? false,
          },
          relationships: {
            pslfProfile: { data: { type: 'pslf-profile', id: pslfProfile.id } },
          },
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    }

    return debugForms
      .map((df) => this.store.peekRecord('pslf-form', df.id) as PslfFormModel)
      .filter(Boolean);
  }

  // ---------------------------------------------------------------------------
  // Account / loan pushing
  // ---------------------------------------------------------------------------

  private unloadAccounts(person: PersonModel): void {
    const accounts = person.accounts?.slice() ?? [];
    for (const account of accounts) {
      for (const loan of account.loans?.slice() ?? []) {
        loan.unloadRecord();
      }
      account.unloadRecord();
    }
  }

  private pushDebugAccounts(person: PersonModel, debugState: PslfDebugState): void {
    this.unloadAccounts(person);

    // Group loans by servicer so each servicer gets its own account + financial institution
    const loansByServicer = new Map<string, { debugLoan: DebugLoan; index: number }[]>();
    debugState.debugLoans.forEach((debugLoan, index) => {
      const key = debugLoan.servicer ?? 'Mohela';
      const group = loansByServicer.get(key) ?? [];
      group.push({ debugLoan, index });
      loansByServicer.set(key, group);
    });

    let groupIndex = 0;
    for (const [servicerName, loans] of loansByServicer) {
      const accountId = `debug-nslds-${person.id}-${groupIndex}`;
      const financialInstitutionId = `debug-fi-${person.id}-${groupIndex}`;

      const loanResources = loans.map(({ debugLoan, index }) => ({
        id: `debug-loan-${index + 1}`,
        type: 'loan',
        attributes: {
          isActive: true,
          observation: {
            'loan-name': debugLoan.name,
            at: new Date().toISOString(),
            balance: debugLoan.currentBalance,
            'principal-balance': debugLoan.currentBalance,
            'interest-rate': 5.5,
            'forgiveness-program-qualified': debugLoan.qualifyingPayments,
            'is-paid-off': false,
            'loan-type': debugLoan.loanType,
            'repayment-plan-derived': 'ibr',
            status: 'active',
          },
        },
        relationships: {
          account: { data: { type: 'account', id: accountId } },
        },
      }));

      const financialInstitutionResource = {
        id: financialInstitutionId,
        type: 'financial-institution',
        attributes: {
          name: servicerName,
          isActive: true,
        },
      };

      const accountBalance = loans.reduce((sum, l) => sum + l.debugLoan.currentBalance, 0);

      this.store.push({
        data: {
          id: accountId,
          type: 'account',
          attributes: {
            isActive: true,
            observation: {
              provider: 'ObservableProvider.NSLDS',
              balance: accountBalance,
            },
          },
          relationships: {
            person: { data: { type: 'person', id: person.id } },
            financialInstitution: {
              data: { type: 'financial-institution', id: financialInstitutionId },
            },
            loans: {
              data: loanResources.map((l) => ({ type: 'loan', id: l.id })),
            },
          },
        },
        included: [...loanResources, financialInstitutionResource],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);

      groupIndex++;
    }
  }

  // ---------------------------------------------------------------------------
  // Recommendation pushing
  // ---------------------------------------------------------------------------

  private pushDebugRecommendation(): RecommendationModel {
    const id = 'debug-recommendation';
    const existing = this.store.peekRecord('recommendation', id) as RecommendationModel | undefined;
    if (existing) existing.unloadRecord();

    this.store.push({
      data: {
        id,
        type: 'recommendation',
        attributes: {
          hasErrorInCalcs: false,
          requestingApp: 'PSLF',
          createdAt: new Date().toISOString(),
          bestPlanSummary: {
            'payoff-amount': [DEBUG_RECOMMENDATION.bestPayoffAmount],
            'term-months': [DEBUG_RECOMMENDATION.bestTermMonths],
            'monthly-payments': [DEBUG_RECOMMENDATION.bestMonthlyPayments],
            'total-interest-paid': [DEBUG_RECOMMENDATION.bestTotalInterestPaid],
            'estimated-amount-forgiven': [DEBUG_RECOMMENDATION.bestRemLoanBalance],
          },
          currentPlanSummary: {
            'payoff-amount': [DEBUG_RECOMMENDATION.currentPayoffAmount],
            'term-months': [DEBUG_RECOMMENDATION.currentTermMonths],
            'monthly-payments': [DEBUG_RECOMMENDATION.currentMonthlyPayments],
            'total-interest-paid': [DEBUG_RECOMMENDATION.currentTotalInterestPaid],
            'estimated-amount-forgiven': [DEBUG_RECOMMENDATION.currentRemLoanBalance],
          },
          bestPlanNextSteps: [],
          bestPlanTableHeadings: [],
          currentPlanTableHeadings: [],
          inputSummary: {},
          outputSummary: {},
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);

    return this.store.peekRecord('recommendation', id) as RecommendationModel;
  }
}
